import { auth } from '@mxa-argos/core/config/firebase';
import { markLoggingOut } from '@mxa-argos/modules/auth/services/force-logout';
import type { AuthProvider } from '@refinedev/core';
import {
  getRedirectResult,
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
  signInWithRedirect,
  signOut,
} from 'firebase/auth';

const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ hd: 'maximiliana.es' });

const validateEmail = (email: string | null): boolean => {
  return email?.endsWith('@maximiliana.es') ?? false;
};

export const authProvider: AuthProvider = {
  login: async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);

      if (!validateEmail(result.user.email)) {
        await signOut(auth);
        return {
          success: false,
          error: { name: 'LoginError', message: 'Solo cuentas @maximiliana.es' },
        };
      }

      return { success: true, redirectTo: '/' };
    } catch (error: unknown) {
      const firebaseError = error as { code?: string; message?: string };

      if (firebaseError.code === 'auth/popup-blocked') {
        await signInWithRedirect(auth, googleProvider);
        return { success: true, redirectTo: '/' };
      }

      return {
        success: false,
        error: { name: 'LoginError', message: firebaseError.message ?? 'Error de autenticacion' },
      };
    }
  },

  logout: async () => {
    markLoggingOut();
    await signOut(auth);
    return { success: true, redirectTo: '/login' };
  },

  check: async () => {
    try {
      const redirectResult = await getRedirectResult(auth);
      if (redirectResult?.user) {
        if (!validateEmail(redirectResult.user.email)) {
          await signOut(auth);
          return { authenticated: false, redirectTo: '/login' };
        }
      }
    } catch {
      // No redirect pending, continue
    }

    return new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(auth, (user) => {
        unsubscribe();
        if (user && validateEmail(user.email)) {
          resolve({ authenticated: true });
        } else {
          resolve({ authenticated: false, redirectTo: '/login' });
        }
      });
    });
  },

  getIdentity: async () => {
    const user = auth.currentUser;
    if (!user) return null;

    return {
      id: user.uid,
      name: user.displayName ?? user.email?.split('@')[0] ?? 'Usuario',
      email: user.email,
      avatar: user.photoURL,
    };
  },

  onError: async (error) => {
    const httpError = error as { status?: number; message?: string };

    if (httpError.status === 401) {
      const isSessionExpired = httpError.message?.includes('expired');
      if (isSessionExpired) {
        return {
          logout: true,
          error: {
            name: 'SessionExpired',
            message: 'Han pasado 14 días desde el último inicio de sesión. Vuelve a autenticarte.',
          },
        };
      }
      return { logout: true };
    }

    return { error };
  },
};
